import React from 'react';
import { useAuth } from '@/auth';
import { checkAllPermissions, checkAnyPermissions, PERMISSIONS } from '@/auth/permissions';
import { useCurrentCategoria } from '@/hooks/useCurrentCategoria';
import { useSubtramiteForm } from '../../../hooks/useSubtramiteForm';
import { saveOtros } from '../../../services/tramiteService';
import SubtramiteForm from '../../SubtramiteForm';
import { withRequiredTramite, WithRequiredTramiteProps } from './withRequiredTramite';


// Función para mapear categorías a permisos específicos
const getCategoryUploadPermissions = (categoria: string | null): string[] => {
  const categoryPermissionMap: Record<string, string[]> = {
    'certificados': [PERMISSIONS.UPLOAD_CERTIFICADOS_FILES.name],
    'presupuestos': [PERMISSIONS.UPLOAD_PRESUPUESTOS_FILES.name],
    'planos': [PERMISSIONS.UPLOAD_PLANOS_FILES.name],
    'estudio-titulo': [PERMISSIONS.UPLOAD_ESTUDIO_TITULO_FILES.name],
    'tramites': [PERMISSIONS.UPLOAD_TRAMITES_FILES.name],
  };

  if (!categoria) return [];

  return categoryPermissionMap[categoria] || [];
};

// Función para mapear categorías a permisos de visualización
const getCategoryViewPermissions = (categoria: string | null): string[] => {
  const categoryPermissionMap: Record<string, string[]> = {
    'certificados': [PERMISSIONS.VIEW_CERTIFICADOS_FILES.name],
    'presupuestos': [PERMISSIONS.VIEW_PRESUPUESTOS_FILES.name],
    'planos': [PERMISSIONS.VIEW_PLANOS_FILES.name],
    'estudio-titulo': [PERMISSIONS.VIEW_ESTUDIO_TITULO_FILES.name],
    'tramites': [PERMISSIONS.VIEW_TRAMITES_FILES.name],
  };

  if (!categoria) return [];

  return categoryPermissionMap[categoria] || [];
};

const Otros: React.FC<WithRequiredTramiteProps> = ({ tramite, canChangeName }) => {

  const categoria = useCurrentCategoria();

  const {
    data,
    loading,
    init,
    isUpdate,
    setIsUpdate,
    handleChange,
    handleSubmit,
    handleFinalizarSubtramite,
    handleReabrirSubtramite
  } = useSubtramiteForm({
    subtramiteKey: 'otros',
    defaultData: {
      nombre: categoria === "estudio-titulo" ? 'Documentos' : 'Otros',
      categoria
    },
    saveFunction: saveOtros,
    successMessage: `${tramite?.otros?.nombre || 'Otros'} Guardado con éxito`,
    errorTitle: `Error al grabar ${tramite?.otros?.nombre || 'Otros'}`,
  });

  const { user } = useAuth();
  const userPermissions = user?.permissions || [];
  const canManageTramites = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITES.name]);

  // Verificar permisos específicos por categoría
  const categoryUploadPermissions = getCategoryUploadPermissions(tramite?.otros?.categoria ?? categoria);
  const categoryViewPermissions = getCategoryViewPermissions(tramite?.otros?.categoria ?? categoria);

  const canUploadByCategoryPermissions = categoryUploadPermissions.length > 0
    ? checkAnyPermissions(userPermissions, categoryUploadPermissions)
    : false;

  const canViewByCategoryPermissions = categoryViewPermissions.length > 0
    ? checkAnyPermissions(userPermissions, categoryViewPermissions)
    : false;

  const canUpload = canManageTramites || canUploadByCategoryPermissions;
  const canView = canManageTramites || canUploadByCategoryPermissions || canViewByCategoryPermissions;

  // Configuración de campos básicos
  const fields = [
    {
      type: 'toggle',
      name: 'habilitado',
      label: 'Habilitado:',
      inline: true,
      visible: canManageTramites && !isUpdate,
    },
    {
      type: 'text',
      name: 'nombre',
      label: 'Nombre:',
      inline: true,
      visible: canManageTramites && !isUpdate && canChangeName,
    },
    {
      type: 'text',
      name: 'categoria',
      label: 'Categoría:',
      inline: true,
      visible: false
    },
    {
      type: 'date',
      name: 'fecini',
      label: 'Fecha de inicio:',
      inline: true,
      visible: canManageTramites && !isUpdate,
    },
  ];

  // No hay campos de archivos específicos, solo el OtherFileList genérico
  const fileFields: any[] = [];

  const btnGuardarVisible = canUpload;

  return (
    <SubtramiteForm
      title={data.nombre || 'Otros'}
      data={data}
      loading={loading}
      init={init}
      isUpdate={isUpdate}
      setIsUpdate={setIsUpdate}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      handleFinalizarSubtramite={handleFinalizarSubtramite}
      handleReabrirSubtramite={handleReabrirSubtramite}
      btnGuardarVisible={btnGuardarVisible}
      fields={fields}
      fileFields={fileFields}
      subtramiteKey="otros"
      canUploadOtros={canUpload}
      canViewOtros={canView}
    />
  );
}

export default withRequiredTramite(Otros);
